import { useMemo } from "react";
import { useTenant } from "hooks/settings";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  useListUploadedFilesQuery,
  UploadedFileMeta,
} from "src/store/api/generatedApi";
import { UploadedFileType } from "components/file/upload/uploadedFileType";

dayjs.extend(utc);
dayjs.extend(timezone);

interface InventoryFileStatus {
  fileType: UploadedFileType;
  file: UploadedFileMeta | null;
  isPresent: boolean;
  isProcessed: boolean;
}

interface RecentInventoryFilesResult {
  fileStatuses: InventoryFileStatus[];
  allFilesPresent: boolean;
  allFilesProcessed: boolean;
  isLoading: boolean;
  refetch: () => void;
}

/**
 * Hook to check for recent inventory files (uploaded today).
 * Returns status for MB51, MB52, and Railcar inventory files.
 */
export const useRecentInventoryFiles = (
  since: string
): RecentInventoryFilesResult => {
  const tenant = useTenant();

  // Query for all three file types
  const {
    data: mb51Files = [],
    isLoading: mb51Loading,
    refetch: refetchMb51,
  } = useListUploadedFilesQuery({
    tenantName: tenant,
    types: ["mb51_deliveries"],
    deleted: false,
    since,
    limit: 1,
    order: "desc",
  });

  const {
    data: mb52Files = [],
    isLoading: mb52Loading,
    refetch: refetchMb52,
  } = useListUploadedFilesQuery({
    tenantName: tenant,
    types: ["mb52_inventory"],
    deleted: false,
    since,
    limit: 1,
    order: "desc",
  });

  const {
    data: railcarFiles = [],
    isLoading: railcarLoading,
    refetch: refetchRailcar,
  } = useListUploadedFilesQuery({
    tenantName: tenant,
    types: ["railcar_inventory"],
    deleted: false,
    since,
    limit: 1,
    order: "desc",
  });

  const refetch = () => {
    void refetchMb51();
    void refetchMb52();
    void refetchRailcar();
  };

  const fileStatuses: InventoryFileStatus[] = useMemo(() => {
    const mb51File = mb51Files[0] ?? null;
    const mb52File = mb52Files[0] ?? null;
    const railcarFile = railcarFiles[0] ?? null;

    return [
      {
        fileType: "mb51_deliveries" as UploadedFileType,
        file: mb51File,
        isPresent: mb51File !== null,
        isProcessed: mb51File?.state === "parsed",
      },
      {
        fileType: "mb52_inventory" as UploadedFileType,
        file: mb52File,
        isPresent: mb52File !== null,
        isProcessed: mb52File?.state === "parsed",
      },
      {
        fileType: "railcar_inventory" as UploadedFileType,
        file: railcarFile,
        isPresent: railcarFile !== null,
        isProcessed: railcarFile?.state === "parsed",
      },
    ];
  }, [mb51Files, mb52Files, railcarFiles]);

  const allFilesPresent = useMemo(
    () => fileStatuses.every((status) => status.isPresent),
    [fileStatuses]
  );

  const allFilesProcessed = useMemo(
    () =>
      fileStatuses.every((status) => status.isPresent && status.isProcessed),
    [fileStatuses]
  );

  return {
    fileStatuses,
    allFilesPresent,
    allFilesProcessed,
    isLoading: mb51Loading || mb52Loading || railcarLoading,
    refetch,
  };
};

/**
 * Get user-friendly display name for file type
 */
export const getFileTypeDisplayName = (
  fileType: UploadedFileType,
  t: (key: string) => string
): string => {
  switch (fileType) {
    case "mb51_deliveries":
      return t("mb51Deliveries");
    case "mb52_inventory":
      return t("mb52Inventory");
    case "railcar_inventory":
      return t("railcarInventory");
    case "inventory_report":
      return t("inventoryReport");
    default:
      return fileType;
  }
};

/**
 * Get the start of today in the tenant's local timezone
 */
export const getTodayStartInTenantLocalTime = (timezone: string): string => {
  return dayjs().tz(timezone).startOf("day").toISOString();
};
